import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragIndicator } from "@mui/icons-material";
import { Box, type SxProps, type Theme } from "@mui/material";
import type { ReactNode } from "react";

type SortableItemProps = {
	id: string;
	children: ReactNode;
};

export const SortableItem = ({ id, children }: SortableItemProps) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		position: "relative" as const,
	};

	return (
		<Box ref={setNodeRef} style={style}>
			<SortableItemContext.Provider value={{ attributes, listeners }}>
				{children}
			</SortableItemContext.Provider>
		</Box>
	);
};

import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { createContext, useContext } from "react";

type SortableItemContextType = {
	attributes: DraggableAttributes;
	listeners: SyntheticListenerMap | undefined;
} | null;

const SortableItemContext = createContext<SortableItemContextType>(null);

type DragHandleProps = {
	sx?: SxProps<Theme>;
};

export const DragHandle = ({ sx }: DragHandleProps) => {
	const context = useContext(SortableItemContext);

	if (!context) {
		return null;
	}

	const { attributes, listeners } = context;

	return (
		<Box
			component="span"
			sx={{
				display: "flex",
				alignItems: "center",
				cursor: "grab",
				touchAction: "none",
				"&:active": {
					cursor: "grabbing",
				},
				...sx,
			}}
			{...attributes}
			{...listeners}
		>
			<DragIndicator sx={{ color: "text.disabled" }} />
		</Box>
	);
};
